/**
 * What a page last loaded on this visit (a goal, a product, a partner, a library item), so going back to it
 * shows it at once while it loads again behind, as a phone app keeps its screens. Any change the person makes
 * forgets all of it, and so does a load that set out before that change: what it brings may be from before
 * it (specs/formal/kept-pages). Switching business reloads the page, which forgets it too.
 */
const kept = new Map<string, unknown>();
const onTheWay = new Map<string, Promise<unknown>>();
/** Moves on with each change, so a load that set out before it is not kept after it. */
let era = 0;

/** What `key` last loaded, if anything. */
export function lastLoaded<T>(key: string): T | null {
  return (kept.get(key) as T | undefined) ?? null;
}

/** Load `key` and keep what it brings; a load already on its way (one a finger started) is joined, not repeated. */
export function loadKept<T>(key: string, fetch: () => Promise<T>): Promise<T> {
  const going = onTheWay.get(key) as Promise<T> | undefined;
  if (going) return going;
  const from = era;
  const loading = fetch()
    .then((value) => {
      if (from === era) kept.set(key, value);
      return value;
    })
    .finally(() => {
      if (onTheWay.get(key) === loading) onTheWay.delete(key);
    });
  onTheWay.set(key, loading);
  return loading;
}

/** Start loading what the next page needs as a finger rests on its row, so it opens ready. */
export function prefetchKept<T>(key: string, fetch: () => Promise<T>): void {
  loadKept(key, fetch).catch(() => {});
}

/** A change was made: forget everything kept and every load still on its way. */
export function forgetKept(): void {
  era += 1;
  kept.clear();
  onTheWay.clear();
}
